/**
 * Enum hooks
 *
 * In-memory cache (per browser session) so repeated calls to the same
 * namespace don't fire duplicate network requests.
 *
 * Usage:
 *   const { options, loading, error } = useEnumOptions('user.roles');
 *   const { tree, loading } = useEnumTree('property.types');
 *   const { children, loading } = useEnumChildren('property.types', 'housing');
 *   const { namespaces, loading } = useEnumNamespaces({ domain: 'user' });
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getEnumOptions,
  getEnumTree,
  getEnumChildren,
  getEnumDescendants,
  getEnumAncestors,
  listEnumNamespaces,
} from '../api/enumApi';

// ─── In-memory cache ──────────────────────────────────────────────────────────

const cache = new Map();
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

function getCached(key) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

function setCached(key, data) {
  cache.set(key, { data, expiresAt: Date.now() + CACHE_TTL_MS });
}

/**
 * Warm the cache for a namespace's options without rendering a component.
 * Used by the startup preloader so nav/menus are ready on first paint.
 * Safe to call repeatedly — skips the network if already cached.
 * @returns {Promise<Array>}
 */
export async function prefetchEnumOptions(namespace) {
  if (!namespace) return [];
  const cacheKey = `enum:${namespace}:options`;
  const cached = getCached(cacheKey);
  if (cached) return cached;
  const data = await getEnumOptions(namespace);
  setCached(cacheKey, data);
  return data;
}

/**
 * Warm the cache for a hierarchical namespace's tree.
 * @returns {Promise<Array>}
 */
export async function prefetchEnumTree(namespace) {
  if (!namespace) return [];
  const cacheKey = `enum:${namespace}:tree`;
  const cached = getCached(cacheKey);
  if (cached) return cached;
  const data = await getEnumTree(namespace);
  setCached(cacheKey, data);
  return data;
}

/** Clear all cached enum data (call after admin writes) */
export function clearEnumCache(namespace) {
  if (namespace) {
    for (const key of cache.keys()) {
      if (key.startsWith(`enum:${namespace}`)) cache.delete(key);
    }
  } else {
    cache.clear();
  }
}

// ─── useEnumOptions ───────────────────────────────────────────────────────────

/**
 * Returns flat list of active options for a namespace.
 * Perfect for <select> / dropdown components.
 *
 * @param {string} namespace
 * @returns {{ options: Array, loading: boolean, error: Error|null, reload: Function }}
 */
export function useEnumOptions(namespace) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (force = false) => {
    if (!namespace) return;
    const cacheKey = `enum:${namespace}:options`;

    if (!force) {
      const cached = getCached(cacheKey);
      if (cached) {
        setOptions(cached);
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      const data = await getEnumOptions(namespace);
      setCached(cacheKey, data);
      setOptions(data);
    } catch (err) {
      console.error(`[useEnumOptions] ${namespace}:`, err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [namespace]);

  useEffect(() => { load(); }, [load]);

  return { options, loading, error, reload: () => load(true) };
}

// ─── useEnumTree ──────────────────────────────────────────────────────────────

/**
 * Returns tree structure for hierarchical namespaces (e.g. property.types).
 *
 * @param {string} namespace
 * @returns {{ tree: Array, loading: boolean, error: Error|null, reload: Function }}
 */
export function useEnumTree(namespace) {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (force = false) => {
    if (!namespace) return;
    const cacheKey = `enum:${namespace}:tree`;

    if (!force) {
      const cached = getCached(cacheKey);
      if (cached) {
        setTree(cached);
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      const data = await getEnumTree(namespace);
      setCached(cacheKey, data);
      setTree(data);
    } catch (err) {
      console.error(`[useEnumTree] ${namespace}:`, err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [namespace]);

  useEffect(() => { load(); }, [load]);

  return { tree, loading, error, reload: () => load(true) };
}

// ─── useEnumChildren ──────────────────────────────────────────────────────────

/**
 * Returns direct children of a parent value.
 * Re-fetches automatically when parentValue changes.
 *
 * @param {string} namespace
 * @param {string|null} parentValue  pass null to skip fetch
 * @returns {{ children: Array, loading: boolean, error: Error|null }}
 */
export function useEnumChildren(namespace, parentValue) {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!namespace || !parentValue) {
      setChildren([]);
      return;
    }

    const cacheKey = `enum:${namespace}:children:${parentValue}`;
    const cached = getCached(cacheKey);
    if (cached) {
      setChildren(cached);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getEnumChildren(namespace, parentValue)
      .then((data) => {
        if (!cancelled) {
          setCached(cacheKey, data);
          setChildren(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(`[useEnumChildren] ${namespace}/${parentValue}:`, err);
          setError(err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [namespace, parentValue]);

  return { children, loading, error };
}

// ─── useEnumDescendants ───────────────────────────────────────────────────────

/**
 * Returns all descendants of a parent value (flat list, ordered by depth).
 *
 * @param {string} namespace
 * @param {string|null} parentValue
 */
export function useEnumDescendants(namespace, parentValue) {
  const [descendants, setDescendants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!namespace || !parentValue) {
      setDescendants([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    getEnumDescendants(namespace, parentValue)
      .then((data) => { if (!cancelled) setDescendants(data); })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [namespace, parentValue]);

  return { descendants, loading, error };
}

// ─── useEnumAncestors ────────────────────────────────────────────────────────

/**
 * Returns the ancestor chain of a value (root-first).
 *
 * @param {string} namespace
 * @param {string|null} value
 */
export function useEnumAncestors(namespace, value) {
  const [ancestors, setAncestors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!namespace || !value) {
      setAncestors([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    getEnumAncestors(namespace, value)
      .then((data) => { if (!cancelled) setAncestors(data); })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [namespace, value]);

  return { ancestors, loading, error };
}

// ─── useEnumNamespaces ────────────────────────────────────────────────────────

/**
 * Returns namespace summaries (for admin catalogue / EnumManager).
 *
 * @param {{ domain?, category?, search? }} filters
 */
export function useEnumNamespaces(filters = {}) {
  const [namespaces, setNamespaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const filterKey = JSON.stringify(filters);

  const load = useCallback(async (force = false) => {
    const cacheKey = `enum:catalogue:${filterKey}`;
    if (!force) {
      const cached = getCached(cacheKey);
      if (cached) {
        setNamespaces(cached);
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      const data = await listEnumNamespaces(filters);
      setCached(cacheKey, data);
      setNamespaces(data);
    } catch (err) {
      console.error('[useEnumNamespaces]:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => { load(); }, [load]);

  return { namespaces, loading, error, reload: () => load(true) };
}

// ─── useEnumValue (resolve a single value → label/color) ─────────────────────

/**
 * Resolves a single enum value to its option object (label, color, flags, etc.)
 * Uses the cached options list — no extra network request if options are already loaded.
 *
 * @param {string} namespace
 * @param {string|null} value
 * @returns {{ option: Object|null, loading: boolean }}
 */
export function useEnumValue(namespace, value) {
  const { options, loading } = useEnumOptions(namespace);
  const option = options.find((o) => o.value === value) ?? null;
  return { option, loading };
}
